'use client'
import { Skeleton } from '@mantine/core'
import { ImageWithFallback } from '@/components'
import { useRouter } from 'next/navigation'
import { getPropertyDetailsImageUrl } from '@/utils/imageUtils'
import { toSlug } from '@/utils/common'
import PropTypes from 'prop-types'

function PropertyDetailsCard({ data, loading, slug }) {
  const router = useRouter()

  const handlePhotoClick = () => {
    if (data?.property_uid) {
      router.push(`/${toSlug(data?.city)}/${slug}/show-more`)
    }
  }

  // Skip main photo for now, only use other photos - use optimized quality
  const allImages = [
    // data?.main_photo,
    ...(data?.other_photos?.map((img) => getPropertyDetailsImageUrl(img)) ||
      []),
  ]

  // If no other photos, use main photo as fallback
  const hasOtherPhotos = data?.other_photos?.length > 0
  const displayImages = hasOtherPhotos
    ? allImages
    : [data?.main_photo].filter(Boolean)

  // Determine the image to preload & prioritize - use optimized quality
  const mainImage = hasOtherPhotos
    ? getPropertyDetailsImageUrl(data?.other_photos?.[0])
    : data?.main_photo

  const alt = hasOtherPhotos ? 'First photo' : 'Main photo'

  return (
    <>
      {/* The main photo below uses `priority`, so Next.js injects the correct
          <link rel="preload"> for the optimized LCP image automatically. */}
      <div className='flex justify-between w-full gap-[22px] h-[512px] max-lg:h-[450px] mt-6'>
        {/* Main Photo - now showing first other_photo instead of main_photo */}
        <button
          className='flex-[58%] rounded-[20px] overflow-hidden cursor-pointer'
          onClick={handlePhotoClick}
          aria-label='View all property photos'
        >
          {loading || !data ? (
            <Skeleton visible={loading} height='100%' bdrs={10} />
          ) : mainImage ? (
            <ImageWithFallback
              src={mainImage}
              alt={alt}
              className='object-cover w-full h-full rounded-[10px]'
              width={1000}
              height={1000}
              priority={true} // ✅ Important for LCP
            />
          ) : (
            <div className='w-full h-full bg-gray-100 rounded-[10px] flex items-center justify-center text-sm text-gray-500'>
              Image not available
            </div>
          )}
        </button>

        {/* Other Photos */}
        <div className='flex-[42%] flex flex-col gap-[22px] w-full h-full relative'>
          {loading || !data ? (
            <div className='flex flex-col gap-[20px] h-full'>
              <div className='flex gap-[22px] h-[50%]'>
                <Skeleton
                  visible={loading}
                  height='100%'
                  width='100%'
                  radius={10}
                  flex={1}
                />
                <Skeleton
                  visible={loading}
                  height='100%'
                  width='100%'
                  radius={10}
                  flex={1}
                />
              </div>
              <div className='flex gap-[22px] h-[50%]'>
                <Skeleton
                  visible={loading}
                  height='100%'
                  width='100%'
                  radius={10}
                  flex={1}
                />
                <Skeleton
                  visible={loading}
                  height='100%'
                  width='100%'
                  radius={10}
                  flex={1}
                />
              </div>
            </div>
          ) : hasOtherPhotos && data?.other_photos?.length > 1 ? (
            data.other_photos
              .slice(1, 5) // Skip first photo since it's shown as main
              .reduce((rows, _, i, arr) => {
                if (i % 2 === 0) rows.push(arr.slice(i, i + 2))
                return rows
              }, [])
              .map((row, rowIndex) => (
                <div key={rowIndex} className='flex gap-[22px] h-[48%]'>
                  {row.map((img, colIndex) => {
                    const isLast =
                      rowIndex * 2 + colIndex ===
                      Math.min(data.other_photos.length - 1, 4) - 1 // -1 because we skip first photo

                    return (
                      <div
                        key={colIndex}
                        className='relative cursor-pointer'
                        onClick={handlePhotoClick}
                      >
                        <ImageWithFallback
                          src={getPropertyDetailsImageUrl(img)}
                          alt={`Other photo ${rowIndex}-${colIndex}`}
                          className='h-full rounded-[20px] w-[251px] object-cover'
                          width={500}
                          height={500}
                        />
                        {isLast && displayImages?.length > 5 && (
                          <button
                            className='absolute inset-0 bg-black/50 text-[#FFFFFF] flex items-center justify-center text-base font-semibold rounded-[20px] cursor-pointer'
                            onClick={(e) => {
                              e.stopPropagation()
                              handlePhotoClick()
                            }}
                            aria-label='Show more photos'
                          >
                            Show more photos
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>
              ))
          ) : (
            <p className='text-sm text-gray-500'>
              {!hasOtherPhotos
                ? 'Only main photo available'
                : 'Other Photos Not Available'}
            </p>
          )}
        </div>
      </div>
    </>
  )
}

PropertyDetailsCard.propTypes = {
  slug: PropTypes.string.isRequired,
  loading: PropTypes.bool,

  data: PropTypes.shape({
    property_uid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    city: PropTypes.string.isRequired,
    main_photo: PropTypes.string,

    // ✅ Explicitly validated
    other_photos: PropTypes.arrayOf(PropTypes.string).isRequired,
  }),
}

PropertyDetailsCard.defaultProps = {
  loading: false,
  data: {
    other_photos: [],
  },
}

export default PropertyDetailsCard
